import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home24Regular,
  Book24Regular,
  HatGraduation24Regular,
  Alert24Regular,
  Navigation24Regular,
  Navigation24Filled,
  Class24Regular,
  TaskListSquareLtr24Regular,
  History24Regular,
  Settings24Regular,
  PeopleTeam24Regular,
  Info24Regular,
} from '@fluentui/react-icons';
import { makeStyles, tokens, Tooltip, Text } from '@fluentui/react-components';
import { usePermissions } from '../../contexts/PermissionContext';
import logoImage from '../../assets/logo-v.png';

const useStyles = makeStyles({
  sidebar: {
    height: '100vh',
    backgroundColor: '#1e1e1e',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '8px',
    boxShadow: tokens.shadow8,
    zIndex: 100,
    transition: 'width 0.3s ease',
  },
  sidebarCollapsed: {
    width: '48px',
    alignItems: 'center',
  },
  sidebarExpanded: {
    width: '220px',
  },
  navItems: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  navItem: {
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    opacity: 0.7,
    transition: 'all 0.2s',
    position: 'relative',
    marginBottom: '4px',
    paddingLeft: '12px',
    paddingRight: '12px',
    '&:hover': {
      opacity: 1,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  navItemCollapsed: {
    justifyContent: 'center',
    paddingLeft: '0',
    paddingRight: '0',
  },
  navItemActive: {
    opacity: 1,
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: '8px',
      bottom: '8px',
      width: '3px',
      backgroundColor: '#0078d4',
    },
  },
  navItemContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  navItemLabel: {
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightRegular,
    color: '#ffffff',
    whiteSpace: 'nowrap',
  },
  toggleButton: {
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#ffffff',
    opacity: 0.7,
    transition: 'all 0.2s',
    paddingLeft: '12px',
    paddingRight: '12px',
    marginBottom: '8px',
    '&:hover': {
      opacity: 1,
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  toggleButtonCollapsed: {
    justifyContent: 'center',
    paddingLeft: '0',
    paddingRight: '0',
  },
  brandingContainer: {
    display: 'flex',
    alignItems: 'center',
    paddingBottom: '12px',
    paddingTop: '4px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    marginTop: 'auto',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  brandingCollapsed: {
    justifyContent: 'center',
    paddingLeft: '0',
    paddingRight: '0',
  },
  brandingExpanded: {
    paddingLeft: '12px',
    paddingRight: '35px',
    gap: '12px',
  },
  logoImage: {
    width: '32px',
    height: '32px',
    objectFit: 'contain',
    flexShrink: 0,
  },
  brandingTextContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5px',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    transition: 'all 0.2s ease',
  },
  brandingText: {
    fontSize: '10px',
    color: 'rgba(255, 255, 255, 0.15)',
    fontWeight: tokens.fontWeightRegular,
    lineHeight: '1.2',
    transition: 'color 0.2s ease',
  },
  brandingTextHover: {
    color: 'rgba(255, 255, 255, 0.6)',
  },
  companyName: {
    fontSize: '11px',
    color: 'rgba(255, 255, 255, 0.2)',
    fontWeight: tokens.fontWeightSemibold,
    transition: 'color 0.2s ease',
  },
  companyNameHover: {
    color: 'rgba(255, 255, 255, 0.7)',
  },
});

interface NavItem {
  path: string;
  icon: React.ReactElement;
  label: string;
  screenKey: string;
}

const navItems: NavItem[] = [
  {
    path: '/',
    icon: <Home24Regular />,
    label: 'Dashboard',
    screenKey: 'dashboard',
  },
  {
    path: '/history',
    icon: <History24Regular />,
    label: 'Historico',
    screenKey: 'history',
  },
  {
    path: '/tracking',
    icon: <HatGraduation24Regular />,
    label: 'Operacoes Educacionais',
    screenKey: 'tracking',
  },
  {
    path: '/section-tracking',
    icon: <Class24Regular />,
    label: 'Classes',
    screenKey: 'section-tracking',
  },
  {
    path: '/education',
    icon: <Book24Regular />,
    label: 'Microsoft 365 Education',
    screenKey: 'education',
  },
  {
    path: '/dlq',
    icon: <TaskListSquareLtr24Regular />,
    label: 'Gerenciamento da Fila',
    screenKey: 'dlq',
  },
  {
    path: '/access-control',
    icon: <PeopleTeam24Regular />,
    label: 'Controle de Acesso',
    screenKey: 'access-control',
  },
  {
    path: '/alerts',
    icon: <Alert24Regular />,
    label: 'Alertas',
    screenKey: 'alerts',
  },
  {
    path: '/maintenance',
    icon: <Settings24Regular />,
    label: 'Manutencao',
    screenKey: 'maintenance',
  },
  {
    path: '/about',
    icon: <Info24Regular />,
    label: 'Sobre',
    screenKey: 'about',
  },
];

const STORAGE_KEY = 'sidebar-collapsed';

export const Sidebar: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, isAdmin } = usePermissions();

  // Initialize from localStorage
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : true; // Default collapsed
  });

  const [brandingHover, setBrandingHover] = useState(false);

  // Save to localStorage whenever collapsed state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(collapsed));
  }, [collapsed]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleToggle = () => {
    setCollapsed(!collapsed);
  };

  const handleBrandingClick = () => {
    window.open('https://www.valueit.com.br', '_blank');
  };

  // Filtra itens de navegacao baseado em permissoes
  const visibleNavItems = navItems.filter((item) => {
    // Admin ve tudo
    if (isAdmin) return true;
    // Verifica permissao para o item
    return hasPermission(item.screenKey);
  });

  return (
    <div
      className={`${styles.sidebar} ${collapsed ? styles.sidebarCollapsed : styles.sidebarExpanded}`}
    >
      <div className={styles.navItems}>
        {visibleNavItems.map((item) => {
          const isActive = location.pathname === item.path;
          const navItemClasses = `${styles.navItem} ${collapsed ? styles.navItemCollapsed : ''} ${isActive ? styles.navItemActive : ''}`;

          const content = (
            <div
              className={navItemClasses}
              onClick={() => handleNavigation(item.path)}
            >
              <div className={styles.navItemContent}>
                {item.icon}
                {!collapsed && <Text className={styles.navItemLabel}>{item.label}</Text>}
              </div>
            </div>
          );

          // Show tooltip only when collapsed
          if (collapsed) {
            return (
              <Tooltip key={item.path} content={item.label} relationship="label" positioning="after">
                {content}
              </Tooltip>
            );
          }

          return <React.Fragment key={item.path}>{content}</React.Fragment>;
        })}
      </div>

      <div
        className={`${styles.brandingContainer} ${collapsed ? styles.brandingCollapsed : styles.brandingExpanded}`}
        onClick={handleBrandingClick}
        onMouseEnter={() => setBrandingHover(true)}
        onMouseLeave={() => setBrandingHover(false)}
      >
        <img src={logoImage} alt="Value iT" className={styles.logoImage} />
        {!collapsed && (
          <div className={styles.brandingTextContainer}>
            <Text className={`${styles.brandingText} ${brandingHover ? styles.brandingTextHover : ''}`}>
              Desenvolvimento
            </Text>
            <Text className={`${styles.companyName} ${brandingHover ? styles.companyNameHover : ''}`}>
              Value iT
            </Text>
          </div>
        )}
      </div>

      <Tooltip
        content={collapsed ? 'Expandir' : 'Recolher'}
        relationship="label"
        positioning="after"
      >
        <div
          className={`${styles.toggleButton} ${collapsed ? styles.toggleButtonCollapsed : ''}`}
          onClick={handleToggle}
        >
          {collapsed ? <Navigation24Regular /> : <Navigation24Filled />}
          {!collapsed && <Text className={styles.navItemLabel}>Recolher</Text>}
        </div>
      </Tooltip>
    </div>
  );
};

export default Sidebar;
